/**
 * Helpers for Cognito auth when the app runs inside the Chrome extension.
 * Login opens in a new tab (side panel cannot navigate to external URLs);
 * user and redirect path are shared via chrome.storage.
 *
 * We prefer opening the Cognito auth URL directly in the new tab (https)
 * so blockers that block chrome-extension:// don't block the first load.
 * The callback still loads our extension URL after Cognito redirects.
 */

import { OidcClient } from 'oidc-client-ts';
import { getOidcConfig } from './oidcConfig';

const POST_LOGIN_KEY = 'wc.auth.post_login_redirect';
const OIDC_USER_KEY = 'wc.oidc.user';

function isExtension(): boolean {
    return (
        typeof __IN_EXTENSION__ !== 'undefined' &&
        __IN_EXTENSION__ &&
        typeof chrome !== 'undefined' &&
        !!chrome.storage
    );
}

export function isExtensionAuth(): boolean {
    return isExtension();
}

export function setRedirectInExtensionStorage(redirectPath: string): Promise<void> {
    if (!isExtension() || !chrome?.storage?.session) return Promise.resolve();
    return new Promise(resolve => {
        chrome.storage.session.set({ [POST_LOGIN_KEY]: redirectPath }, () => resolve());
    });
}

export function getRedirectFromExtensionStorage(): Promise<string | null> {
    if (!isExtension() || !chrome?.storage?.session) return Promise.resolve(null);
    return new Promise(resolve => {
        chrome.storage.session.get([POST_LOGIN_KEY], result => {
            const path = result[POST_LOGIN_KEY] as string | undefined;
            resolve(path ?? null);
        });
    });
}

export function clearRedirectFromExtensionStorage(): Promise<void> {
    if (!isExtension() || !chrome?.storage?.session) return Promise.resolve();
    return new Promise(resolve => {
        chrome.storage.session.remove(POST_LOGIN_KEY, () => resolve());
    });
}

export function persistUserToExtensionStorage(userStorageString: string): Promise<void> {
    if (!isExtension() || !chrome?.storage?.local) return Promise.resolve();
    return new Promise(resolve => {
        chrome.storage.local.set({ [OIDC_USER_KEY]: userStorageString }, () => resolve());
    });
}

export function getStoredUserFromExtensionStorage(): Promise<string | null> {
    if (!isExtension() || !chrome?.storage?.local) return Promise.resolve(null);
    return new Promise(resolve => {
        chrome.storage.local.get([OIDC_USER_KEY], result => {
            const str = result[OIDC_USER_KEY] as string | undefined;
            resolve(str ?? null);
        });
    });
}

export function clearUserFromExtensionStorage(): Promise<void> {
    if (!isExtension() || !chrome?.storage?.local) return Promise.resolve();
    return new Promise(resolve => {
        chrome.storage.local.remove(OIDC_USER_KEY, () => resolve());
    });
}

/**
 * Config for the background script to run OAuth via chrome.identity.launchWebAuthFlow.
 * Uses https://<id>.chromiumapp.org/ as redirect so Chrome intercepts it (no ERR_BLOCKED_BY_CLIENT).
 */
export function getExtensionAuthConfig(): {
    clientId: string;
    region: string;
    userPoolId: string;
    scopes: string;
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
} | null {
    if (!isExtension()) return null;
    try {
        const full = getOidcConfig();
        const region = (import.meta.env.VITE_COGNITO_REGION as string) ?? '';
        const userPoolId = (import.meta.env.VITE_COGNITO_USER_POOL_ID as string) ?? '';
        const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
        let authorizationEndpoint: string | undefined;
        let tokenEndpoint: string | undefined;
        if (domain?.trim()) {
            const base = domain.startsWith('http')
                ? domain.replace(/\/+$/, '')
                : `https://${domain}`;
            authorizationEndpoint = `${base}/oauth2/authorize`;
            tokenEndpoint = `${base}/oauth2/token`;
        }
        return {
            clientId: full.client_id,
            region,
            userPoolId,
            scopes: full.scope ?? 'openid email profile',
            authorizationEndpoint,
            tokenEndpoint,
        };
    } catch {
        return null;
    }
}

/**
 * Start auth natively in the extension via launchWebAuthFlow (no chrome-extension:// redirect).
 * Resolves when the flow finishes; token is stored in chrome.storage and React will see it via listener.
 */
export function startExtensionAuth(redirectPath: string): Promise<{ ok: boolean; error?: string }> {
    if (!isExtension() || !chrome?.runtime?.sendMessage) {
        return Promise.resolve({ ok: false, error: 'Not in extension' });
    }
    const config = getExtensionAuthConfig();
    if (!config) return Promise.resolve({ ok: false, error: 'Missing auth config' });
    return new Promise(resolve => {
        chrome.runtime.sendMessage(
            { type: 'START_AUTH', config, redirectPath: redirectPath || '/' },
            (response: unknown) => {
                const err = (chrome as { runtime?: { lastError?: { message?: string } } }).runtime
                    ?.lastError;
                if (err) {
                    resolve({ ok: false, error: err.message });
                    return;
                }
                const r = response as { ok?: boolean; error?: string } | undefined;
                resolve({ ok: r?.ok ?? false, error: r?.error });
            }
        );
    });
}

/** @deprecated Use startExtensionAuth; kept for fallback. */
export async function getSigninRedirectUrl(): Promise<string | null> {
    if (!isExtension()) return null;
    try {
        const config = getOidcConfig();
        const client = new OidcClient(config);
        const request = await client.createSigninRequest({ request_type: 'si:r' });
        return request?.url ?? null;
    } catch {
        return null;
    }
}

/** @deprecated Auth uses launchWebAuthFlow; kept for fallback. */
export function openAuthTabWithUrl(_url: string): void {
    if (!isExtension() || !chrome?.runtime?.sendMessage) return;
    chrome.runtime.sendMessage({ type: 'OPEN_AUTH_TAB', url: _url });
}

/** @deprecated Auth uses launchWebAuthFlow; kept for fallback. */
export function openAuthTab(): void {
    if (!isExtension() || !chrome?.runtime?.getURL) return;
    const url = chrome.runtime.getURL('app/index.html#/login?openInTab=1');
    chrome.runtime.sendMessage({ type: 'OPEN_AUTH_TAB', url });
}

export function addExtensionStorageListener(
    callback: (userStorageString: string | null) => void
): () => void {
    if (!isExtension() || !chrome?.storage?.onChanged?.addListener) return () => {};

    const listener = (changes: { [key: string]: { newValue?: unknown } }, areaName: string) => {
        if (areaName !== 'local' || !changes[OIDC_USER_KEY]) return;
        callback((changes[OIDC_USER_KEY].newValue as string) ?? null);
    };

    chrome.storage.onChanged.addListener(listener);
    return () => chrome.storage.onChanged.removeListener(listener);
}
